import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../contexts/LangContext";
import { useLogSession } from "../../contexts/LogSessionContext";
import { fishDatabase } from "../../data/fish_db";
import { storageService, shouldShowExtraInfo } from "../../services/storage.service";
import { AppSettings, ExtraInfo as ExtraInfoType } from "../../types/data";

type MealLocation = ExtraInfoType['meal_location'];
type FishSource = ExtraInfoType['fish_source'];
type PreparedBy = ExtraInfoType['prepared_by'];
type CoIngredients = ExtraInfoType['co_ingredients'];

// Legacy: parse META tags from ingredients string (backward compatibility)
const parseMetaLegacy = (text: string): any => {
  const metaMatch = text.match(/\[META: (.+?)\]/);
  return metaMatch ? JSON.parse(metaMatch[1]) : null;
};

// Legacy: strip META tags from ingredients string
const stripMetaLegacy = (text: string): string => {
  return text.replace(/\n?\[META: .+?\]/g, '');
};

export default function ExtraInfo() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { currentDayRecord, currentMealRecord, newFishItem, setNewFishItem } =
    useLogSession();

  // Local state for additional info
  const [mealLocation, setMealLocation] = useState<MealLocation | null>(null);
  const [fishSource, setFishSource] = useState<FishSource | null>(null);
  const [preparedBy, setPreparedBy] = useState<PreparedBy | null>(null);
  const [coIngredients, setCoIngredients] = useState<CoIngredients | null>(null);
  const [peopleSharingCount, setPeopleSharingCount] = useState<string>('1');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      const appSettings = await storageService.getSettings();
      setSettings(appSettings);
    };
    loadSettings();
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  // Initialize state from existing data if user is returning to this page
  useEffect(() => {
    // Try new extra_info field first, fall back to legacy META in ingredients
    const info = newFishItem?.extra_info ?? parseMetaLegacy(newFishItem?.ingredients || '');
    if (info) {
      if (info.meal_location) setMealLocation(info.meal_location);
      if (info.fish_source) setFishSource(info.fish_source);
      if (info.prepared_by) setPreparedBy(info.prepared_by);
      if (info.co_ingredients) setCoIngredients(info.co_ingredients);
      if (info.people_sharing) setPeopleSharingCount(String(info.people_sharing));
    }
  }, []);

  const checkLogSession = () => {
    if (!currentDayRecord) {
      navigate("../any-fish-today", { replace: true });
      return;
    } else if (!currentMealRecord) {
      navigate("../which-meal", { replace: true });
      return;
    } else if (!newFishItem) {
      navigate("../which-fish", { replace: true });
      return;
    }
  };

  useEffect(() => {
    checkLogSession();
  }, [currentDayRecord, currentMealRecord, newFishItem, navigate]);

  const fish = newFishItem
    ? fishDatabase.find((f) => f.id === newFishItem.fish_id)
    : undefined;
  const hasSoftBone = fish?.has_soft_bone || false;

  // If the Extra Info page would render nothing (no soft-bone toggle and every
  // extra-info field disabled), skip it entirely.
  useEffect(() => {
    if (newFishItem && settings && !shouldShowExtraInfo(settings, hasSoftBone)) {
      navigate("../review-fish-item", { replace: true });
    }
  }, [settings, newFishItem, hasSoftBone, navigate]);

  if (!newFishItem) {
    return <div className="p-8 text-red-600">{t("error_noFishItem")}</div>;
  }

  // Master toggle: whether to ask for additional info at all
  const askExtraInfo = settings?.surveyAskExtraInfo ?? true;
  // Whether to show the notes/ingredients textarea
  const showNotesArea = askExtraInfo && (settings?.surveyShowNotes ?? false);

  // Check if any content section is visible (soft bone or a chip)
  const hasAnyContentVisible = hasSoftBone || (
    askExtraInfo && (
      (settings?.surveyShowMealLocation ?? true) ||
      (settings?.surveyShowFishSource ?? true) ||
      (settings?.surveyShowPreparedBy ?? true) ||
      (settings?.surveyShowCoIngredients ?? true) ||
      (settings?.surveyShowPeopleSharing ?? true)
    )
  );

  // If notes are enabled but no other content is visible, always show the textarea
  const forceShowTextarea = showNotesArea && !hasAnyContentVisible;

  const toggle = (key: keyof typeof newFishItem) => {
    setNewFishItem({ ...newFishItem, [key]: !newFishItem[key] });
  };

  const handleNoteChange = (note: string) => {
    setNewFishItem({ ...newFishItem, ingredients: note });
  };

  const clearAll = () => {
    setMealLocation(null);
    setFishSource(null);
    setPreparedBy(null);
    setCoIngredients(null);
    setPeopleSharingCount('1');
  };

  const handleSkip = () => {
    // Clear all selections
    clearAll();
    
    setNewFishItem({ 
      ...newFishItem, 
      ingredients: newFishItem.ingredients || '',
      extra_info: undefined,
    });
    
    navigate("../review-fish-item");
  };

  const handleNext = () => {
    // Build structured extra_info
    const extraInfo: ExtraInfoType = {};
    if (mealLocation) extraInfo.meal_location = mealLocation;
    if (fishSource) extraInfo.fish_source = fishSource;
    if (preparedBy) extraInfo.prepared_by = preparedBy;
    if (coIngredients) extraInfo.co_ingredients = coIngredients;
    if (peopleSharingCount && peopleSharingCount !== '1') {
      extraInfo.people_sharing = parseInt(peopleSharingCount);
    }
    
    setNewFishItem({ 
      ...newFishItem, 
      ingredients: newFishItem.ingredients || '',
      extra_info: Object.keys(extraInfo).length > 0 ? extraInfo : undefined,
    });
    
    navigate("../review-fish-item");
  };

  // Chip selection component
  const ChipSelector = ({ 
    label, 
    options, 
    selected, 
    onSelect 
  }: { 
    label: string; 
    options: { value: string; label: string; icon?: string }[]; 
    selected: string | null | undefined; 
    onSelect: (value: string) => void;
  }) => (
    <div className="w-full bg-slate-200/50 p-4 rounded-2xl backdrop-blur-sm">
      <label className="text-lg font-semibold text-gray-700 mb-4 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={`
              px-4 py-2 rounded-full text-sm font-medium transition-all
              ${selected === option.value 
                ? 'bg-blue-500 text-white shadow-md scale-105' 
                : 'bg-white text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            {option.icon && <span className="mr-1">{option.icon}</span>}
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full flex flex-col grow">
      <div className="w-full grow flex flex-col items-center gap-6 text-center px-scr pt-8 pb-8">
        <h1 className="text-2xl font-bold text-rose-700">
          {t("extraInfo_Heading")}
        </h1>

        <div className="w-full flex flex-col items-center gap-4">
          {/* Soft Bone Toggle (if applicable) - Changed to Yes/No toggle */}
          {hasSoftBone && (
            <div className="w-full">
              <label 
                htmlFor="soft-bone-toggle" 
                className="relative cursor-pointer select-none group"
              >
                <div 
                  className={`
                    relative overflow-hidden
                    pl-2 pr-4 py-2 rounded-full
                    border-2 transition-all duration-300
                    ${newFishItem.consumed_soft_bone 
                      ? 'border-blue-500 shadow-lg shadow-blue-200' 
                      : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                    }
                  `}
                >
                  {/* Animated background */}
                  <div 
                    className={`
                      absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600
                      transition-transform duration-500 ease-in-out
                      ${newFishItem.consumed_soft_bone ? 'translate-x-0' : '-translate-x-full'}
                    `}
                  />
                  
                  {/* Content */}
                  <div className="relative flex items-center gap-3">
                    {/* Custom checkbox with animated checkmark icon */}
                    <div className="relative flex-shrink-0">
                      <input
                        id="soft-bone-toggle"
                        type="checkbox"
                        checked={newFishItem.consumed_soft_bone}
                        onChange={() => toggle("consumed_soft_bone")}
                        className="sr-only"
                      />
                      <div 
                        className={`
                          w-8 h-8 rounded-full flex items-center justify-center
                          transition-all duration-300
                          ${newFishItem.consumed_soft_bone 
                            ? 'bg-white scale-110' 
                            : 'bg-white border-2 border-gray-400 group-hover:border-gray-500'
                          }
                        `}
                      >
                        {newFishItem.consumed_soft_bone ? (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-6 w-6 text-blue-600" 
                            viewBox="0 0 24 24" 
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                          >
                            <polyline 
                              points="20 6 9 17 4 12"
                              className="origin-center"
                            />
                          </svg>
                        ) : (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-5 w-5 text-gray-400" 
                            viewBox="0 0 24 24" 
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        )}
                      </div>
                    </div>
                    
                    {/* Label text */}
                    <div className="flex flex-col items-start">
                      <span 
                        className={`
                          font-semibold text-base transition-colors duration-300
                          ${newFishItem.consumed_soft_bone ? 'text-white' : 'text-gray-800'}
                        `}
                      >
                        {t("settings_extraInfo_SoftBoneToggle")}
                      </span>
                      <span 
                        className={`
                          text-sm transition-colors duration-300
                          ${newFishItem.consumed_soft_bone ? 'text-blue-100' : 'text-gray-500'}
                        `}
                      >
                        {newFishItem.consumed_soft_bone ? t("settings_extraInfo_SoftBoneYes") : t("settings_extraInfo_SoftBoneNo")}
                      </span>
                    </div>
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Quick Selection Chips */}
          <div className="w-full space-y-4">
            {/* Contextual Clear All Button */}
            {(mealLocation || fishSource || preparedBy || coIngredients || peopleSharingCount !== '1') && (
              <div className="flex justify-end w-full">
                <button
                  type="button"
                  onClick={clearAll}
                  className="button-xs flex gap-1 items-center text-sm bg-gray-100 !text-red-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  <span>{t("extraInfo_ClearAll")}</span>
                </button>
              </div>
            )}
            
            {askExtraInfo && (settings?.surveyShowMealLocation ?? true) && (
              <ChipSelector
                label={t("extraInfo_MealLocation")}
                selected={mealLocation}
                onSelect={(val) => setMealLocation(val as MealLocation)}
                options={[
                  { value: 'home', label: t("extraInfo_Location_Home"), icon: '🏠' },
                  { value: 'restaurant', label: t("extraInfo_Location_Restaurant"), icon: '🍽️' },
                  { value: 'market_stall', label: t("extraInfo_Location_MarketStall"), icon: '🏪' },
                  { value: 'outdoor', label: t("extraInfo_Location_Outdoor"), icon: '🌳' },
                  { value: 'other', label: t("extraInfo_Location_Other"), icon: '📍' },
                ]}
              />
            )}

            {askExtraInfo && (settings?.surveyShowFishSource ?? true) && (
              <ChipSelector
                label={t("extraInfo_FishSource")}
                selected={fishSource}
                onSelect={(val) => setFishSource(val as FishSource)}
                options={[
                  { value: 'market', label: t("extraInfo_Source_Market"), icon: '🛒' },
                  { value: 'self_caught', label: t("extraInfo_Source_SelfCaught"), icon: '🎣' },
                  { value: 'farmed', label: t("extraInfo_Source_Farmed"), icon: '🏞️' },
                  { value: 'gift', label: t("extraInfo_Source_Gift"), icon: '🎁' },
                  { value: 'other', label: t("extraInfo_Source_Other"), icon: '❓' },
                ]}
              />
            )}

            {askExtraInfo && (settings?.surveyShowPreparedBy ?? true) && (
              <ChipSelector
                label={t("extraInfo_PreparedBy")}
                selected={preparedBy}
                onSelect={(val) => setPreparedBy(val as PreparedBy)}
                options={[
                  { value: 'self', label: t("extraInfo_PreparedBy_Self"), icon: '👤' },
                  { value: 'family', label: t("extraInfo_PreparedBy_Family"), icon: '👨‍👩‍👧' },
                  { value: 'restaurant', label: t("extraInfo_PreparedBy_Restaurant"), icon: '👨‍🍳' },
                  { value: 'other', label: t("extraInfo_PreparedBy_Other"), icon: '👥' },
                ]}
              />
            )}

            {askExtraInfo && (settings?.surveyShowCoIngredients ?? true) && (
              <ChipSelector
                label={t("extraInfo_CoIngredients")}
                selected={coIngredients}
                onSelect={(val) => setCoIngredients(val as CoIngredients)}
                options={[
                  { value: 'potato', label: t("extraInfo_CoIngredients_Potato"), icon: '🥔' },
                  { value: 'other_vegetables', label: t("extraInfo_CoIngredients_OtherVegetables"), icon: '🥬' },
                  { value: 'no_vegetable', label: t("extraInfo_CoIngredients_NoVegetable"), icon: '🚫' },
                ]}
              />
            )}

            {/* People Sharing */}
            {askExtraInfo && (settings?.surveyShowPeopleSharing ?? true) && (
              <div className="w-full">
                <label className="text-sm font-semibold text-gray-700 mb-2 block">
                  {t("extraInfo_PeopleSharing")}
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={peopleSharingCount}
                  onChange={(e) => setPeopleSharingCount(e.target.value)}
                  className="w-full input-base !bg-slate-200/50"
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 mt-1">{t("extraInfo_PeopleSharingHint")}</p>
              </div>
            )}
          </div>

          {/* Advanced/Optional Section - Show collapse button only if there are visible sections above */}
          {showNotesArea && !forceShowTextarea && (
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-600 font-medium flex items-center gap-1 hover:text-blue-700"
            >
              <span>{showAdvanced ? t("extraInfo_HideAdvanced") : t("extraInfo_ShowAdvanced")}</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth={2} 
                stroke="currentColor" 
                className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
          )}

          {/* Show textarea either when expanded or when forced (all sections hidden) */}
          {showNotesArea && (showAdvanced || forceShowTextarea) && (
            <div className="w-full bg-slate-200/50 rounded-xl shadow-sm p-4 space-y-4">
              {/* Cooking Notes / Ingredients */}
              <div className="w-full">
                <label className="text-sm font-semibold text-gray-700 mb-2 block text-left">
                  {t("extraInfo_IngredientsLabel")}
                </label>
                <textarea
                  className="w-full input-base custom_input !rounded-lg !bg-white !text-base min-h-32 !text-left !px-3 !py-2 border-2 border-gray-200 focus:border-blue-500"
                  value={newFishItem.ingredients || ''}
                  onChange={(e) => handleNoteChange(e.target.value)}
                  placeholder={t("extraInfo_IngredientsPlaceholder")}
                />
              </div>
            </div>
          )}
          
        </div>      
      </div>
      <div className="bottom-nav-bar flex justify-between items-center gap-2 w-full sticky bottom-0 px-6 py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
          {/* Skip Button */}
          <button
            type="button"
            onClick={handleSkip}
            className="button-small bg-rose-500"
          >
            {t("extraInfo_Skip")}
          </button>
          
          
          {/* Next Button */}
          <button
            className="flex items-center justify-center gap-2 button-small button-bg-grad-2"
            onClick={handleNext}
          >
            <span>{t("extraInfo_Next")}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
              />
            </svg>
          </button>
          
          
      </div>
    </div>
  );
}
